/*
 * Parts the client, job and visit pages share: crew chips, visit status, notes, files, the line items editor, the Team
 * sheet and the Edit schedule sheet, and the "just this visit or this and future ones" question.
 */
import {
  centsToDecimal,
  LINE_FREQUENCIES,
  toCents,
  type Cents,
  type FileRow,
  type HomeAnswers,
  type NoteRow,
  type PersonChip,
  type VisitCard,
  yardLabel,
} from "@steward/shared";
import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ApiError, pictureUrl } from "../api/client";
import {
  useAddNote,
  useCapacity,
  useMoveVisit,
  useRemoveFile,
  useRemoveNote,
  useRestoreNote,
  useUpdateNote,
  useUpload,
  useUploading,
  useVisitCrew,
  useVisits,
  type LineInput,
  type NoteOn,
  type UploadInput,
} from "../api/hooks";
import { Choice, Combobox, InputText } from "./fields";
import { ago, Banner, Button, Card, EmptyState, IconButton, StatusLabel, type Status } from "./parts";
import type { FutureKey } from "./repeat";
import { Icon } from "./Icon";
import { Modal, useToast } from "./overlays";
import { useYardAsk } from "./YardAsk";
import { HeldPhotos } from "./HeldPhotos";
import { holdPhoto, worthKeeping } from "./photoOutbox";
import { dayDate, minutesWords, moneyExact, monthName, plural, todayIn } from "./format";
import { useSession } from "../app/session";
import { busyPeople, DayBoard } from "../screens/SchedulePhone";

/** The server's own words for a failed change, or a plain fallback. */
export const errText = (e: unknown, fallback = "That didn't save. Try again.") => (e instanceof ApiError ? e.message : fallback);

export function CrewChips({ crew, none = "No crew yet" }: { crew: PersonChip[]; none?: string }) {
  return (
    <div className="crew-chips" aria-label="Crew">
      {crew.length ? (
        crew.map((c) => (
          <span key={c.id} style={c.color ? { ["--person" as string]: c.color } : undefined} className="person-chip">
            {c.name}
          </span>
        ))
      ) : (
        <span className="none">{none}</span>
      )}
    </div>
  );
}

const STATUS: Record<string, [Status, string]> = {
  scheduled: ["informative", "Scheduled"],
  in_progress: ["warning", "Started"],
  done: ["success", "Done"],
  skipped: ["inactive", "Skipped"],
  cancelled: ["critical", "Cancelled"],
};
export function VisitStatus({ status, late }: { status: string; late?: boolean }) {
  if (late && status === "scheduled") return <StatusLabel status="critical">Not done yet</StatusLabel>;
  const [s, words] = STATUS[status] ?? ["inactive", status];
  return <StatusLabel status={s}>{words}</StatusLabel>;
}

/** Just this visit, or this one and the ones after it. Only asked when the job repeats. */
export function ScopeChoice({ value, onChange }: { value: "this" | "future"; onChange: (v: "this" | "future") => void }) {
  return (
    <Choice
      label="Change which visits"
      value={value}
      onChange={onChange}
      options={[
        { value: "this", label: "Just this visit" },
        { value: "future", label: "This and future visits", hint: "Past visits stay as they were" },
      ]}
    />
  );
}

// Notes --------------------------------------------------------------------------------------------------------------

export function NotesCard({
  on,
  id,
  notes,
  editable,
  title = "Notes",
  officeOnly = false,
}: {
  on: NoteOn;
  id: string;
  notes: NoteRow[];
  editable: boolean;
  title?: string;
  /** No "who sees it" choice: the crew never sees these (requests). */
  officeOnly?: boolean;
}) {
  const add = useAddNote();
  const update = useUpdateNote();
  const remove = useRemoveNote();
  const restore = useRestoreNote();
  const toast = useToast();
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState<"office" | "crew">("office");
  const [writing, setWriting] = useState(false);
  const save = () =>
    add.mutate(
      { on, id, note: { body: body.trim(), visibility, pinned: false } },
      {
        onSuccess: () => {
          setBody("");
          setWriting(false);
          toast("Note saved");
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Card
      title={title}
      action={
        editable && !writing ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setWriting(true)}>
            Add note
          </Button>
        ) : null
      }
      flush
    >
      {writing ? (
        <div className="note-form">
          <textarea
            aria-label="Note"
            rows={3}
            value={body}
            autoFocus
            placeholder="Type the note"
            onChange={(e) => setBody(e.target.value)}
          />
          {officeOnly ? null : (
            <div className="row">
              <Choice
                label="Who sees it"
                value={visibility}
                onChange={setVisibility}
                options={[
                  { value: "office", label: "Office only" },
                  { value: "crew", label: "Office and crew" },
                ]}
              />
            </div>
          )}
          <div className="row end">
            <Button type="tertiary" size="small" onClick={() => setWriting(false)}>
              Cancel
            </Button>
            <Button size="small" loading={add.isPending} disabled={!body.trim()} onClick={save}>
              Save note
            </Button>
          </div>
        </div>
      ) : null}
      {notes.length ? (
        <div className="todo">
          {notes.map((n) => (
            <div className="item note" key={n.id}>
              <Icon name={n.pinned ? "pin" : "note"} />
              <div className="what">
                <span className="note-body">{n.body}</span>
                <span>
                  {n.authorName ?? "Someone"} · {ago(n.createdAt)}
                  {n.visibility === "crew" ? " · Crew can see it" : ""}
                </span>
              </div>
              {editable ? (
                <div className="row tight">
                  <IconButton
                    icon="pin"
                    label={n.pinned ? "Unpin" : "Pin to the top"}
                    size={18}
                    onClick={() => update.mutate({ id: n.id, patch: { pinned: !n.pinned } })}
                  />
                  <IconButton
                    icon="trash"
                    label="Delete note"
                    size={18}
                    onClick={() =>
                      remove.mutate(n.id, {
                        onSuccess: () => toast("Note deleted", { undo: () => restore.mutate(n.id) }),
                        onError: (e) => toast(errText(e), { kind: "error" }),
                      })
                    }
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : !writing ? (
        <div className="card-empty muted">No notes yet.</div>
      ) : null}
    </Card>
  );
}

// Files and photos ---------------------------------------------------------------------------------------------------

const asBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? (r.result.split(",")[1] ?? "") : "");
    r.onerror = () => reject(new Error("That file could not be read."));
    r.readAsDataURL(f);
  });
const sizeWords = (b: number) =>
  b < 1024 * 1024 ? `${String(Math.max(1, Math.round(b / 1024)))} KB` : `${(b / 1048576).toFixed(1)} MB`;

/** A photo as a small square on its row; a tap shows it big (owner, 2026-10-31: no way to tell the photos apart). */
function FilePic({ file, label }: { file: FileRow; label: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let gone = false;
    void pictureUrl(`files/${file.id}`)
      .then((u) => {
        if (gone) {
          if (u?.startsWith("blob:")) URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setSrc(u);
      })
      .catch(() => undefined);
    return () => {
      gone = true;
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    };
  }, [file.id]);
  return (
    <>
      <button type="button" className="file-pic" aria-label={`See ${label}`} onClick={() => setOpen(true)} disabled={!src}>
        {src ? <img src={src} alt="" /> : <Icon name="camera" />}
      </button>
      <Modal title={label} open={open && Boolean(src)} onClose={() => setOpen(false)} size="large">
        {src ? <img className="file-pic-big" src={src} alt={label} /> : null}
      </Modal>
    </>
  );
}

/** A file still sending: its picture from the phone and a spinner, so a slow connection does not look like nothing happened. */
function SendingFile({ up }: { up: UploadInput }) {
  const { mime, dataBase64, name, room } = up.file;
  const src = useMemo(() => (mime.startsWith("image/") ? `data:${mime};base64,${dataBase64}` : null), [mime, dataBase64]);
  return (
    <div className="item sending" aria-live="polite">
      <span className="file-pic">{src ? <img src={src} alt="" /> : <Icon name="file" />}</span>
      <div className="what">
        <b>{yardLabel(room) ?? name}</b>
        <span>
          <span className="spin" aria-hidden /> {up.on === "visits" ? "Adding photo" : "Adding file"}
        </span>
      </div>
    </div>
  );
}

export function FilesCard({
  on,
  id,
  files,
  editable,
  title,
}: {
  on: "clients" | "visits";
  id: string;
  files: FileRow[];
  editable: boolean;
  title: string;
}) {
  const upload = useUpload();
  const remove = useRemoveFile();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const yard = useYardAsk();
  const sending = useUploading(on, id);
  const pick = (list: FileList | null) => {
    const picked = Array.from(list ?? []);
    if (input.current) input.current.value = "";
    if (!picked.length) return;
    if (on === "visits") yard.ask(picked.length, (room) => void send(picked, room));
    else void send(picked, null);
  };
  /** All at once, so each one shows as Adding in the list right away. */
  const send = async (picked: File[], room: string | null) => {
    const one = async (f: File) => {
      if (f.size > 10 * 1024 * 1024) {
        toast(`${f.name} is too big. The most is 10 MB.`, { kind: "error" });
        return;
      }
      const kind = f.type.startsWith("image/")
        ? ("photo" as const)
        : f.type === "application/pdf"
          ? ("pdf" as const)
          : ("other" as const);
      const file = {
        name: f.name,
        mime: f.type || "application/octet-stream",
        kind,
        dataBase64: await asBase64(f),
        room: room ?? undefined,
      };
      try {
        await upload.mutateAsync({ on, id, file });
        toast(`${f.name} added`);
      } catch (e) {
        // A visit photo that failed for the connection is kept on this phone to send again (photoOutbox.ts).
        if (on === "visits" && worthKeeping(e)) {
          holdPhoto(`visits:${id}`, `visits/${id}/photos`, file, e);
          toast("The photo didn't send. It is kept here. Tap Try again.", { kind: "error" });
        } else toast(errText(e, "That file didn't upload. Try again."), { kind: "error" });
      }
    };
    await Promise.all(picked.map(one));
  };
  return (
    <Card
      title={title}
      action={
        editable ? (
          <>
            <input
              ref={input}
              type="file"
              hidden
              multiple
              accept={on === "visits" ? "image/*" : undefined}
              onChange={(e) => pick(e.target.files)}
            />
            {yard.sheet}
            <Button
              type="tertiary"
              size="small"
              icon={on === "visits" ? "camera" : "plus"}
              loading={upload.isPending}
              onClick={() => input.current?.click()}
            >
              {on === "visits" ? "Add photos" : "Add file"}
            </Button>
          </>
        ) : null
      }
      flush
    >
      {on === "visits" ? <HeldPhotos at={`visits:${id}`} /> : null}
      {files.length || sending.length ? (
        <div className="todo">
          {sending.map((up, i) => (
            <SendingFile key={`sending-${String(i)}`} up={up} />
          ))}
          {files.map((f) => (
            <div className="item" key={f.id}>
              {f.kind === "photo" && f.mime.startsWith("image/") ? (
                <FilePic file={f} label={yardLabel(f.room) ?? f.name} />
              ) : (
                <Icon name={f.kind === "photo" ? "camera" : "file"} />
              )}
              <div className="what">
                <b>{f.name}</b>
                <span>{[yardLabel(f.room), sizeWords(f.size), ago(f.createdAt)].filter(Boolean).join(" · ")}</span>
              </div>
              {editable ? (
                confirming === f.id ? (
                  <div className="row tight confirm-inline">
                    <span>Delete it?</span>
                    <Button type="tertiary" size="small" onClick={() => setConfirming(null)}>
                      Keep
                    </Button>
                    <Button
                      size="small"
                      variation="destructive"
                      loading={remove.isPending}
                      onClick={() =>
                        remove.mutate(f.id, {
                          onSuccess: () => {
                            setConfirming(null);
                            toast("File deleted");
                          },
                          onError: (e) => toast(errText(e), { kind: "error" }),
                        })
                      }
                    >
                      Delete
                    </Button>
                  </div>
                ) : (
                  <IconButton icon="trash" label="Delete file" size={18} onClick={() => setConfirming(f.id)} />
                )
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="card-empty muted">
          {on === "visits" ? "No photos yet." : "No files yet. Add a W-9, contract or photo."}
        </div>
      )}
    </Card>
  );
}

// Line items ---------------------------------------------------------------------------------------------------------

/** A line while it is typed: the price as the dollars typed. */
export interface DraftLine {
  key: string;
  description: string;
  quantity: string;
  price: string;
  frequency: string;
  /** What the line covers, shown under it to the client and the crew. */
  note: string;
  /** The price list's "See what's included" page, kept when the line is saved again. */
  link?: string | null | undefined;
  /** The auto quote the line was priced from, kept when the line is saved again. */
  answers?: HomeAnswers | null;
}
let nextKey = 0;
export const draftLine = (l?: {
  description: string;
  quantity: number;
  unitPriceCents: number;
  frequency: string;
  note?: string | null | undefined;
  link?: string | null | undefined;
  answers?: HomeAnswers | null | undefined;
}): DraftLine => ({
  key: String(++nextKey),
  description: l?.description ?? "",
  quantity: l ? String(l.quantity) : "1",
  price: l ? centsToDecimal(l.unitPriceCents as Cents) : "",
  frequency: l?.frequency ?? "every",
  note: l?.note ?? "",
  ...(l?.link ? { link: l.link } : {}),
  ...(l?.answers ? { answers: l.answers } : {}),
});
const priceOf = (s: string): Cents | null => {
  const n = Number(s.replace(/[$,\s]/g, ""));
  return s.trim() && Number.isFinite(n) ? toCents(n) : null;
};
export const draftTotal = (lines: DraftLine[]) =>
  lines.reduce((sum, l) => sum + Math.round((Number(l.quantity) || 0) * (priceOf(l.price) ?? 0)), 0);
/** The typed lines as the server takes them, or the first problem to fix. */
export function linesOut(lines: DraftLine[]): LineInput[] | string {
  const out: LineInput[] = [];
  for (const l of lines) {
    if (!l.description.trim() && !l.price.trim()) continue;
    if (!l.description.trim()) return "Name every line.";
    const price = priceOf(l.price);
    if (price === null) return `Type a price for ${l.description.trim()}.`;
    const quantity = Number(l.quantity);
    if (!(quantity > 0)) return `Use a number above 0 for how many ${l.description.trim()}.`;
    out.push({
      description: l.description.trim(),
      quantity,
      unitPriceCents: price,
      frequency: l.frequency as LineInput["frequency"],
      note: l.note.trim() || null,
      link: l.link ?? null,
      ...(l.answers ? { answers: l.answers } : {}),
    });
  }
  return out;
}

export const LINE_OFTEN: Record<string, string> = {
  every: "Every visit",
  once: "One time",
  weekly: "Every week",
  biweekly: "Every 2 weeks",
  monthly: "Once a month",
  bimonthly: "Every 2 months",
};

export function LinesEditor({
  lines,
  onChange,
  repeats,
  firstVisit,
}: {
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  /** Job lines on a repeating job can have their own repeat (an extra once a month). */
  repeats?: boolean;
  /** Quote lines on a repeating quote: on every visit, or on the first visit only (frequency "first"). */
  firstVisit?: boolean;
}) {
  const set = (key: string, patch: Partial<DraftLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  return (
    <div className="lines-editor">
      {lines.map((l) => (
        <Fragment key={l.key}>
          <div className={`line-row${repeats || firstVisit ? " repeats" : ""}`}>
            <InputText
              label="Item"
              value={l.description}
              placeholder="House cleaning"
              onChange={(e) => set(l.key, { description: e.target.value })}
            />
            <InputText
              label="How many"
              inputMode="decimal"
              value={l.quantity}
              onChange={(e) => set(l.key, { quantity: e.target.value })}
            />
            <InputText
              label="Price"
              prefix="$"
              inputMode="decimal"
              value={l.price}
              placeholder="0.00"
              onChange={(e) => set(l.key, { price: e.target.value })}
            />
            {repeats ? (
              <Combobox
                label="How often"
                value={l.frequency}
                onChange={(v) => set(l.key, { frequency: v })}
                options={LINE_FREQUENCIES.map((f) => ({
                  value: f,
                  label: f === "once" ? "One time, next visit only" : (LINE_OFTEN[f] ?? f),
                }))}
              />
            ) : firstVisit ? (
              <Combobox
                label="When"
                value={l.frequency === "first" ? "first" : "every"}
                onChange={(v) => set(l.key, { frequency: v })}
                options={[
                  { value: "every", label: "Every visit" },
                  { value: "first", label: "First visit only" },
                ]}
              />
            ) : null}
            <IconButton
              icon="trash"
              label="Remove line"
              size={18}
              onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
            />
          </div>
          <div className="line-note">
            <InputText
              label="What it covers"
              value={l.note}
              placeholder="Kitchen, bathrooms, dusting, floors"
              maxLength={500}
              description={l.link ? "The See what's included link from the price list stays on this line." : undefined}
              onChange={(e) => set(l.key, { note: e.target.value })}
            />
          </div>
        </Fragment>
      ))}
      <div className="row between">
        <Button type="tertiary" size="small" icon="plus" onClick={() => onChange([...lines, draftLine()])}>
          Add line
        </Button>
        <span className="num">
          Total <b>{moneyExact(draftTotal(lines))}</b>
        </span>
      </div>
    </div>
  );
}

interface TableLine {
  id: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  frequency: string;
  note?: string | null | undefined;
  link?: string | null | undefined;
  /** What went into the price ("3 bed, 2 bath, 2 more rooms"). */
  basis?: string | null | undefined;
}

/** Under a line: what it covers, its See what's included link and, for the office and crew, what it was priced for. */
export function LineMore({ line, basis = true }: { line: Pick<TableLine, "note" | "link" | "basis">; basis?: boolean }) {
  if (!line.note && !line.link && !(basis && line.basis)) return null;
  return (
    <div className="muted small line-basis">
      {basis && line.basis ? <div>Priced for {line.basis}</div> : null}
      {line.note ? <div>{line.note}</div> : null}
      {line.link ? (
        <a href={line.link} target="_blank" rel="noreferrer">
          See what's included
        </a>
      ) : null}
    </div>
  );
}

export function LinesTable<L extends TableLine>({
  lines,
  footer,
  onOpen,
  below,
}: {
  lines: L[];
  footer?: ReactNode;
  /** What opening a line does, or null for a line that does not open (a job line priced from the price list opens the
   * auto quote again). */
  onOpen?: ((line: L) => (() => void) | null) | undefined;
  /** What goes under a line, such as how its price was worked out. */
  below?: ((line: L) => ReactNode) | undefined;
}) {
  if (!lines.length) return <div className="card-empty muted">No line items yet.</div>;
  return (
    <table className="data lines">
      <thead>
        <tr>
          <th>Item</th>
          <th className="right">How many</th>
          <th className="right">Price</th>
          <th className="right">Total</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => {
          const open = onOpen?.(l) ?? null;
          return (
            <tr key={l.id}>
              <td>
                {open ? (
                  <button type="button" className="link line-open" onClick={open}>
                    {l.description}
                  </button>
                ) : (
                  l.description
                )}
                {l.frequency !== "every" ? <span className="muted"> · {LINE_OFTEN[l.frequency] ?? l.frequency}</span> : null}
                {below ? below(l) : <LineMore line={l} />}
              </td>
              <td className="right num">{l.quantity}</td>
              <td className="right num">{moneyExact(l.unitPriceCents)}</td>
              <td className="right num">{moneyExact(l.amountCents)}</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3}>{footer}</td>
          <td className="right num">
            <b>{moneyExact(lines.reduce((s, l) => s + l.amountCents, 0))}</b>
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

// Team sheet ---------------------------------------------------------------------------------------------------------

export function TeamSheet({
  open,
  onClose,
  people,
  selected,
  repeating,
  saving,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  people: { id: string; name: string; hint?: string }[];
  selected: string[];
  repeating: boolean;
  saving: boolean;
  onSave: (ids: string[], scope: "this" | "future") => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set(selected));
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"this" | "future">("this");
  const [was, setWas] = useState(open);
  if (open !== was) {
    setWas(open);
    if (open) {
      setPicked(new Set(selected));
      setQ("");
      setScope("this");
    }
  }
  const shown = useMemo(() => people.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase())), [people, q]);
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return (
    <Modal
      title="Team"
      size="full"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => onSave([...picked], repeating ? scope : "this")}>
            Save Team
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="search">
          <Icon name="search" size={18} />
          <input aria-label="Search the team" placeholder="Search the team" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="row between">
          <span className="muted num">{picked.size} selected</span>
          <Button
            type="tertiary"
            size="small"
            onClick={() => setPicked(picked.size === people.length ? new Set() : new Set(people.map((p) => p.id)))}
          >
            {picked.size === people.length ? "Clear all" : "Select all"}
          </Button>
        </div>
        <div className="pick-list">
          {shown.length ? (
            shown.map((p) => (
              <label key={p.id} className="pick">
                <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                <b>{p.name}</b>
                {p.hint ? <span className="muted">{p.hint}</span> : null}
              </label>
            ))
          ) : (
            <EmptyState icon="team" title="Nobody matches that">
              Try fewer letters.
            </EmptyState>
          )}
        </div>
        {repeating ? <ScopeChoice value={scope} onChange={setScope} /> : null}
      </div>
    </Modal>
  );
}

// Edit schedule sheet ------------------------------------------------------------------------------------------------

export function ScheduleSheet({
  open,
  onClose,
  visit,
  onMoved,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  visit: {
    ref: string;
    date: string;
    startTime: string | null;
    endTime: string | null;
    crew: { id: string }[];
    repeating?: boolean;
  };
  onMoved?: (ref: string) => void;
  /** A repeating job's visit was saved: what changed, so the page can ask about the visits after it. */
  onChanged?: (ref: string, date: string, changed: FutureKey[]) => void;
}) {
  const move = useMoveVisit();
  const setCrew = useVisitCrew();
  const toast = useToast();
  const before = visit.crew.map((c) => c.id);
  const [date, setDate] = useState(visit.date);
  const [start, setStart] = useState(visit.startTime ?? "");
  const [end, setEnd] = useState(visit.endTime ?? "");
  const [crew, setCrewIds] = useState(before);
  const [was, setWas] = useState(open);
  if (open !== was) {
    setWas(open);
    if (open) {
      setDate(visit.date);
      setStart(visit.startTime ?? "");
      setEnd(visit.endTime ?? "");
      setCrewIds(before);
    }
  }
  const bad = end && start && end <= start ? "The end is before the start." : null;
  const crewChanged = crew.length !== before.length || crew.some((id) => !before.includes(id));
  const done = (ref: string) => {
    onClose();
    onMoved?.(ref);
    const changed: FutureKey[] = [];
    if (start !== (visit.startTime ?? "") || end !== (visit.endTime ?? "")) changed.push("time");
    if (date !== visit.date) changed.push("repeats");
    if (crewChanged) changed.push("crew");
    if (visit.repeating && changed.length) onChanged?.(ref, date, changed);
    toast("Schedule saved", {
      undo: () =>
        move.mutate(
          { ref, date: visit.date, startTime: visit.startTime, endTime: visit.endTime },
          { onSuccess: (v) => (crewChanged ? setCrew.mutate({ ref: v.ref, crew: before, scope: "this" }) : undefined) },
        ),
    });
  };
  const save = () =>
    move.mutate(
      { ref: visit.ref, date, startTime: start || null, endTime: end || null },
      {
        onSuccess: (v) =>
          crewChanged
            ? setCrew.mutate(
                { ref: v.ref, crew, scope: "this" },
                { onSuccess: (c) => done(c.ref), onError: (e) => toast(errText(e), { kind: "error" }) },
              )
            : done(v.ref),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Modal
      title="Edit schedule"
      size="full"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={move.isPending || setCrew.isPending} disabled={!date || Boolean(bad)} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      {open ? <PickDay value={date} onPick={setDate} self={visit.ref} crew={crew} onCrew={setCrewIds} /> : null}
      <div className="form-grid">
        <InputText label="Start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        <InputText label="End" type="time" value={end} error={bad} onChange={(e) => setEnd(e.target.value)} />
      </div>
      {visit.repeating ? (
        <Banner type="warning">
          This job repeats. Saving changes only this visit. Then you pick what the visits after it take.
        </Banner>
      ) : null}
    </Modal>
  );
}

/** YYYY-MM-DD plus some days, in plain calendar math (no time zone drift). */
function addDays(d: string, n: number): string {
  const t = new Date(`${d}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}

/**
 * A small month calendar for moving a visit: each day shows how many visits are booked (darker means busier, red means
 * full when capacity is on). Pressing a day opens that day underneath like the Schedule's List view, a column per person.
 * Below it, the cleaners chooser picks who goes (each shows how many visits they already have that day).
 */
function PickDay({
  value,
  onPick,
  self,
  crew,
  onCrew,
}: {
  value: string;
  onPick: (d: string) => void;
  self: string;
  crew: string[];
  onCrew: (ids: string[]) => void;
}) {
  const { company } = useSession();
  const [pressed, setPressed] = useState(false);
  const today = todayIn(company.timezone);
  const [month, setMonth] = useState(`${(value || today).slice(0, 7)}-01`);
  const first = addDays(month, -new Date(`${month}T12:00:00Z`).getUTCDay());
  const last = addDays(first, 41);
  const visits = useVisits(company.id, { from: first, to: last });
  const capacity = useCapacity(first, last);
  const shiftMonth = (n: number) => {
    const t = new Date(`${month}T12:00:00Z`);
    t.setUTCMonth(t.getUTCMonth() + n);
    setMonth(t.toISOString().slice(0, 10));
  };

  const byDay = useMemo(() => {
    const m = new Map<string, VisitCard[]>();
    for (const v of visits.data?.visits ?? []) {
      if (v.ref === self || v.status === "skipped" || v.status === "cancelled") continue;
      m.set(v.date, [...(m.get(v.date) ?? []), v]);
    }
    return m;
  }, [visits.data, self]);
  const full = new Set(capacity.data?.on ? capacity.data.days.filter((d) => !d.open).map((d) => d.date) : []);
  const most = Math.max(1, ...[...byDay.values()].map((l) => l.length));

  const people = visits.data?.people ?? [];
  const day = byDay.get(value) ?? [];
  const cleaners = busyPeople(people, visits.data?.visits ?? []).concat(
    people.filter((p) => crew.includes(p.id) && p.role !== "crew"),
  );
  const shown = cleaners.filter((p, i) => cleaners.findIndex((x) => x.id === p.id) === i);
  const toggle = (id: string) => onCrew(crew.includes(id) ? crew.filter((x) => x !== id) : [...crew, id]);
  return (
    <div className="pick-day">
      <div className="pick-day-head">
        <IconButton icon="arrowLeft" label="Month before" size={18} onClick={() => shiftMonth(-1)} />
        <strong>{monthName(month)}</strong>
        <IconButton icon="arrowRight" label="Next month" size={18} onClick={() => shiftMonth(1)} />
      </div>
      <div className="pick-day-grid" role="grid" aria-busy={visits.isPending}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i} className="pick-day-dow">
            {d}
          </span>
        ))}
        {Array.from({ length: 42 }, (_, i) => {
          const d = addDays(first, i);
          const n = byDay.get(d)?.length ?? 0;
          const cls = [
            "pick-day-cell",
            d.slice(0, 7) !== month.slice(0, 7) ? "out" : "",
            d === today ? "today" : "",
            d === value ? "on" : "",
            full.has(d) ? "full" : "",
          ].join(" ");
          return (
            <button
              key={d}
              type="button"
              className={cls}
              style={{ "--load": n / most } as CSSProperties}
              aria-label={`${dayDate(d)}, ${full.has(d) ? "full" : plural(n, "visit")}`}
              aria-pressed={d === value}
              disabled={d < today}
              onClick={() => {
                onPick(d);
                setPressed(true);
              }}
            >
              <span>{Number(d.slice(8))}</span>
              {n ? <small>{n}</small> : null}
            </button>
          );
        })}
      </div>
      {pressed && value ? (
        <div className="pick-day-board">
          <strong>
            {dayDate(value)}
            {full.has(value) ? <span className="pick-day-full"> Full</span> : null}
          </strong>
          <DayBoard
            day={day}
            people={busyPeople(people, day)}
            today={today}
            refreshing={visits.isPlaceholderData}
            onPick={() => undefined}
          />
        </div>
      ) : null}
      {shown.length ? (
        <div className="pick-crew" role="group" aria-label="Cleaners">
          <strong>Cleaners</strong>
          <div className="pick-crew-list">
            {shown.map((p) => {
              const n = day.filter((v) => v.crew.some((c) => c.id === p.id)).length;
              return (
                <button
                  key={p.id}
                  type="button"
                  className="pick-crew-chip"
                  aria-pressed={crew.includes(p.id)}
                  onClick={() => toggle(p.id)}
                >
                  {crew.includes(p.id) ? <Icon name="check" size={14} /> : null}
                  {p.name}
                  {value ? <small>{n ? plural(n, "visit") : "Free"}</small> : null}
                </button>
              );
            })}
          </div>
          {crew.length ? null : <span className="muted">No cleaner picked. The visit goes to No crew yet.</span>}
        </div>
      ) : null}
    </div>
  );
}

export function TimerWords({ startedAt, minutes }: { startedAt: string | null; minutes: number }) {
  const running = startedAt ? Math.max(0, Math.round((Date.now() - Date.parse(startedAt)) / 60_000)) : 0;
  const total = minutes + running;
  return <span className="num">{total ? minutesWords(total) : "Not started"}</span>;
}
